import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Sidebar from "@/layout/Sidebar";
import TopNavbar from "@/layout/TopNavbar";
import MembersTable from "./MembersTable";
import InviteMemberModal from "./InviteMemberModal";
import CustomDropdown from "@/components/ui/CustomDropdown";
import { exportToCsv } from "@/charts/tableUtils";
import { getLocalStorageWithExpiry, showApiError } from "@/utils";
import "./members.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const parseDateToTimestamp = (dateStr) => {
  if (!dateStr) return 0;
  const parsed = Date.parse(dateStr);
  if (!isNaN(parsed)) return parsed;
  const parsedMonthYear = Date.parse(`01 ${dateStr}`);
  if (!isNaN(parsedMonthYear)) return parsedMonthYear;
  return 0;
};

const AdminMembers = () => {
  const { t } = useTranslation();
  const [isPermanentlyExpanded, setIsPermanentlyExpanded] = useState(true);

  const toggleSidebar = () => {
    setIsPermanentlyExpanded((prev) => !prev);
  };

  const userRole = getLocalStorageWithExpiry("role");
  const activeClientId = getLocalStorageWithExpiry("client_id");
  const userEmail =
    getLocalStorageWithExpiry("email") ||
    getLocalStorageWithExpiry("username");

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [showInviteModal, setShowInviteModal] = useState(false);

  // Fetch client members
  const fetchMembers = useCallback(async () => {
    if (!activeClientId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/v1/client/${encodeURIComponent(activeClientId)}/users`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        },
      );

      if (response.ok) {
        const data = await response.json();
        const memberList = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
            ? data.data
            : [];
        setMembers(memberList);
      } else {
        showApiError(
          t("members.fetchError", "Failed to load organization members"),
        );
        setMembers([]);
      }
    } catch (err) {
      console.error("Could not reach backend users endpoint:", err);
      showApiError(
        t("members.networkError", "Network error occurred while fetching members"),
      );
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }, [activeClientId, t]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  // Handle column header sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Filter & sort members
  const filteredAndSortedMembers = useMemo(() => {
    let result = [...members];

    // 1. Text Search Filter
    if (searchTerm.trim()) {
      const q = searchTerm.trim().toLowerCase();
      result = result.filter((m) => {
        const fullName = `${m.firstname || ""} ${m.lastname || ""}`.toLowerCase();
        const email = (m.email || "").toLowerCase();
        const username = (m.username || "").toLowerCase();
        return (
          fullName.includes(q) ||
          email.includes(q) ||
          username.includes(q)
        );
      });
    }

    // 2. Role Filter
    if (roleFilter === "admin") {
      result = result.filter(
        (m) => m.role === "admin" || m.role === "super-admin",
      );
    } else if (roleFilter === "user" || roleFilter === "member") {
      result = result.filter((m) => m.role !== "admin" && m.role !== "super-admin");
    }

    // 3. Sorting
    result.sort((a, b) => {
      let valA = "";
      let valB = "";

      switch (sortField) {
        case "name":
          valA = `${a.firstname || ""} ${a.lastname || ""}`.trim().toLowerCase();
          valB = `${b.firstname || ""} ${b.lastname || ""}`.trim().toLowerCase();
          break;
        case "email":
          valA = (a.email || "").toLowerCase();
          valB = (b.email || "").toLowerCase();
          break;
        case "role":
          valA = a.role === "admin" || a.role === "super-admin" ? "admin" : "member";
          valB = b.role === "admin" || b.role === "super-admin" ? "admin" : "member";
          break;
        case "teams":
          valA = Array.isArray(a.projects) ? a.projects.length : 1;
          valB = Array.isArray(b.projects) ? b.projects.length : 1;
          return sortDirection === "asc" ? valA - valB : valB - valA;
        case "joined": {
          const timeA = a.created_at
            ? typeof a.created_at === "number"
              ? a.created_at
              : Date.parse(a.created_at) || 0
            : parseDateToTimestamp(a.joined);
          const timeB = b.created_at
            ? typeof b.created_at === "number"
              ? b.created_at
              : Date.parse(b.created_at) || 0
            : parseDateToTimestamp(b.joined);
          return sortDirection === "asc" ? timeA - timeB : timeB - timeA;
        }
        case "last_seen": {
          const timeA = a.is_online
            ? Number.MAX_SAFE_INTEGER
            : a.last_seen_timestamp || parseDateToTimestamp(a.last_seen);
          const timeB = b.is_online
            ? Number.MAX_SAFE_INTEGER
            : b.last_seen_timestamp || parseDateToTimestamp(b.last_seen);
          return sortDirection === "asc" ? timeA - timeB : timeB - timeA;
        }
        default:
          valA = (a.firstname || "").toLowerCase();
          valB = (b.firstname || "").toLowerCase();
      }

      if (valA < valB) return sortDirection === "asc" ? -1 : 1;
      if (valA > valB) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return result;
  }, [members, searchTerm, roleFilter, sortField, sortDirection]);

  // Handle Export CSV
  const handleExportCsv = () => {
    if (filteredAndSortedMembers.length === 0) {
      showApiError("No member records to export.");
      return;
    }

    const columns = [
      { key: "display_name", title: "Name" },
      { key: "username", title: "Username" },
      { key: "email", title: "Email" },
      { key: "role", title: "Status" },
      { key: "teams_count", title: "Teams" },
      { key: "joined", title: "Joined" },
      { key: "last_seen", title: "Last seen" },
    ];

    const dataToExport = filteredAndSortedMembers.map((m) => ({
      display_name: `${m.firstname || ""} ${m.lastname || ""}`.trim() || m.username,
      username: m.username || "",
      email: m.email || "",
      role: m.role === "admin" || m.role === "super-admin" ? "Admin" : "Member",
      teams_count: Array.isArray(m.projects) ? m.projects.length : 1,
      joined: m.joined || "",
      last_seen: m.is_online ? "Online" : m.last_seen || "",
    }));

    exportToCsv(dataToExport, columns, `${activeClientId}_Members`);
  };

  const filterOptions = [
    { value: "all", label: t("members.allFilter", "All") },
    { value: "admin", label: t("members.adminsFilter", "Admins") },
    { value: "user", label: t("members.membersFilter", "Members") },
  ];

  // Session validity check: require active client_id and user identification
  if (!activeClientId || !userEmail) {
    return <Navigate to="/login" replace />;
  }

  // Protected Route check: only admin and super-admin can access
  if (userRole !== "admin" && userRole !== "super-admin") {
    return <Navigate to="/geo-dashboard" replace />;
  }

  return (
    <div className="app-container">
      <Sidebar
        currentPage="member"
        isPermanentlyExpanded={isPermanentlyExpanded}
      />
      <main className="content bg-page">
        <div className="container-fluid p-4">
          <TopNavbar
            isPermanentlyExpanded={isPermanentlyExpanded}
            toggleSidebar={toggleSidebar}
          />

          <div className="members-container">
            {/* Page Header */}
            <div className="members-page-header">
              <div>
                <div className="members-title-wrapper">
                  <h1 className="members-title">{t("members.title", "Members")}</h1>
                  <span className="members-active-badge" data-testid="members-active-count">
                    {t("members.activeCount", "Active {{count}}", {
                      count: filteredAndSortedMembers.length,
                    })}
                  </span>
                </div>
                <p className="members-subtitle">
                  {t(
                    "members.subtitle",
                    "Manage organization members, roles, and project access for {{client}}",
                    { client: activeClientId },
                  )}
                </p>
              </div>
            </div>

            {/* Controls Bar: Search, Role Filter, Export CSV, Invite Button */}
            <div className="members-controls-bar">
              <div className="members-controls-left">
                {/* Search Box */}
                <div className="members-search-wrapper">
                  <i className="bi bi-search members-search-icon" aria-hidden="true" />
                  <input
                    type="text"
                    className="members-search-input"
                    placeholder={t("members.searchPlaceholder", "Search by name or email")}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    data-testid="members-search-input"
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      className="members-search-clear"
                      onClick={() => setSearchTerm("")}
                      aria-label="Clear search"
                    >
                      <i className="bi bi-x-circle-fill" />
                    </button>
                  )}
                </div>

                {/* Role Filter Dropdown */}
                <div style={{ minWidth: 120 }}>
                  <CustomDropdown
                    options={filterOptions}
                    value={roleFilter}
                    onChange={(val) => setRoleFilter(val)}
                    ariaLabel="Filter by role"
                    dataTestId="members-role-filter"
                  />
                </div>
              </div>

              <div className="members-controls-right">
                {/* Export CSV */}
                <button
                  type="button"
                  className="btn-export-csv"
                  onClick={handleExportCsv}
                  data-testid="btn-export-csv"
                >
                  <i className="bi bi-download" />
                  <span>{t("members.exportCsv", "Export CSV")}</span>
                </button>

                {/* Primary Action: Invite (Signature Cyan-to-Mint Gradient Pill) */}
                <button
                  type="button"
                  className="btn-invite-pill"
                  onClick={() => setShowInviteModal(true)}
                  data-testid="btn-invite-member"
                >
                  <i className="bi bi-plus-lg" />
                  <span>{t("members.invite", "Invite")}</span>
                </button>
              </div>
            </div>

            {/* Members Table */}
            <MembersTable
              members={filteredAndSortedMembers}
              loading={loading}
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
            />
          </div>

          {/* Scoped Invite Member Modal */}
          <InviteMemberModal
            show={showInviteModal}
            onClose={() => setShowInviteModal(false)}
            clientId={activeClientId}
            adminEmail={userEmail}
            isSuperAdmin={userRole === "super-admin"}
            onUserCreated={fetchMembers}
          />
        </div>
      </main>
    </div>
  );
};

export default AdminMembers;
